import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  LinearProgress,
  Alert,
  IconButton,
  Collapse,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Switch,
  FormControlLabel,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Badge,
  CircularProgress
} from '@mui/material';
import {
  Sync as SyncIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Schedule as ScheduleIcon,
  DataUsage as DataUsageIcon,
  Group as GroupIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Book as BookIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import { authService } from '../services/authService';
import { format } from 'date-fns';

// Create authenticated axios instance
const authenticatedAxios = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
authenticatedAxios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// API functions
const api = {
  getSyncStatus: async () => {
    try {
      // Get counts from different endpoints - request without pagination limits to get totals
      const [coursesRes, groupsRes, studentsRes] = await Promise.all([
        authenticatedAxios.get('/courses'),
        authenticatedAxios.get('/groups?limit=1000'), // Request large limit to get all groups
        authenticatedAxios.get('/students?limit=5000') // Request large limit to get all students
      ]);
      
      // Handle different response formats
      let totalCourses = 0;
      let totalGroups = 0;
      let totalStudents = 0;
      
      // Courses - array response
      if (coursesRes.data) {
        if (Array.isArray(coursesRes.data)) {
          totalCourses = coursesRes.data.length;
        }
      }
      
      // Groups - paginated response
      if (groupsRes.data) {
        if (groupsRes.data.success && groupsRes.data.pagination) {
          totalGroups = groupsRes.data.pagination.total_items || 0;
        } else if (groupsRes.data.success && groupsRes.data.data) {
          totalGroups = Array.isArray(groupsRes.data.data) ? groupsRes.data.data.length : 0;
        }
      }
      
      // Students - paginated response  
      if (studentsRes.data) {
        if (studentsRes.data.success && studentsRes.data.pagination) {
          totalStudents = studentsRes.data.pagination.total_items || 0;
        } else if (studentsRes.data.success && studentsRes.data.data) {
          totalStudents = Array.isArray(studentsRes.data.data) ? studentsRes.data.data.length : 0;
        }
      }
      
      return { 
        data: {
          total_courses: totalCourses,
          total_groups: totalGroups,
          total_students: totalStudents,
          last_sync: new Date().toISOString(), // Will be updated with real sync time
          sync_enabled: true
        }
      };
    } catch (error) {
      console.error('Error fetching sync status:', error);
      return { 
        data: {
          total_courses: 0,
          total_groups: 0, 
          total_students: 0,
          last_sync: null,
          sync_enabled: true
        }
      };
    }
  },
  getAvailableData: () => authenticatedAxios.get('/courses').then(res => ({ 
    data: {
      courses: res.data.data || res.data,
      students: [],
      groups: []
    }
  })),
  getSyncHistory: () => {
    // Mock sync history for now - this would come from a real sync operations table
    return Promise.resolve({ 
      data: [
        {
          id: 1,
          operation_type: 'full',
          status: 'completed',
          created_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          records_processed: 150
        },
        {
          id: 2,
          operation_type: 'students',
          status: 'completed',
          created_at: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
          records_processed: 1454
        }
      ]
    });
  },
  startSync: (data) => {
    if (data.entityType === 'full') {
      return authenticatedAxios.post('/moodle/sync/full').then(res => res.data);
    } else if (data.entityType === 'courses') {
      return authenticatedAxios.post('/moodle/sync/courses').then(res => res.data);
    } else if (data.entityType === 'groups') {
      return authenticatedAxios.post('/moodle/sync/groups').then(res => res.data);
    } else if (data.entityType === 'students') {
      return authenticatedAxios.post('/moodle/sync/students').then(res => res.data);
    } else {
      return authenticatedAxios.post('/moodle/sync/students', { 
        group_id: data.ids?.[0] || null 
      }).then(res => res.data);
    }
  },
  cancelSync: (operationId) => {
    // Mock cancel sync - this would be implemented with real operation management
    return Promise.resolve({ success: true, message: 'Sync cancelled' });
  },
  getSyncSettings: () => {
    // Mock sync settings
    return Promise.resolve({ 
      data: {
        sync_batch_size: 100,
        sync_timeout: 300
      }
    });
  },
  updateSyncSettings: (settings) => {
    // Mock update settings
    return Promise.resolve({ success: true, data: settings });
  }
};

// Sync Status Card Component
const SyncStatusCard = ({ title, count, status, icon, color, onClick }) => (
  <Card 
    sx={{ 
      cursor: onClick ? 'pointer' : 'default',
      '&:hover': onClick ? { transform: 'translateY(-2px)', boxShadow: 3 } : {},
      transition: 'all 0.3s ease'
    }}
    onClick={onClick}
  >
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="h4" component="div" color={color}>
            {count}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
        </Box>
        <Box color={color}>
          {icon}
        </Box>
      </Box>
      {status && (
        <Chip 
          label={status} 
          size="small" 
          color={status === 'synced' ? 'success' : status === 'syncing' ? 'warning' : 'default'}
          sx={{ mt: 1 }}
        />
      )}
    </CardContent>
  </Card>
);

// Sync Progress Component
const SyncProgress = ({ operation, onCancel }) => {
  if (!operation) return null;

  const progress = operation.records_processed && operation.total_records 
    ? (operation.records_processed / operation.total_records) * 100 
    : 0;

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6">
            {operation.operation_type} Sync
          </Typography>
          <Chip 
            label={operation.status} 
            color={operation.status === 'completed' ? 'success' : operation.status === 'running' ? 'warning' : 'default'}
          />
        </Box>
        
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{ height: 8, borderRadius: 4, mb: 1 }}
        />
        
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {operation.records_processed || 0} / {operation.total_records || 0} records
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {Math.round(progress)}%
          </Typography>
        </Box>
        
        {operation.status === 'running' && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<StopIcon />}
            sx={{ mt: 1 }}
            onClick={() => onCancel(operation.id)}
          >
            Cancel
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// Sync History Component
const SyncHistory = ({ history }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" display="flex" alignItems="center">
            <HistoryIcon sx={{ mr: 1 }} />
            Sync History
          </Typography>
          <IconButton onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        
        <Collapse in={expanded}>
          <List dense>
            {(history || []).slice(0, 10).map((operation) => (
              <ListItem key={operation.id} divider>
                <ListItemIcon>
                  {operation.status === 'completed' ? (
                    <CheckCircleIcon color="success" />
                  ) : operation.status === 'failed' ? (
                    <ErrorIcon color="error" />
                  ) : (
                    <WarningIcon color="warning" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={`${operation.operation_type || 'Unknown'} Sync`}
                  secondary={`${operation.created_at ? format(new Date(operation.created_at), 'MMM dd, yyyy HH:mm') : 'Unknown'} - ${operation.status || 'Unknown'}`}
                />
                <ListItemSecondaryAction>
                  <Typography variant="caption" color="text.secondary">
                    {operation.records_processed || 0} records
                  </Typography>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Collapse>
      </CardContent>
    </Card>
  );
};

// Sync Settings Dialog
const SyncSettingsDialog = ({ open, onClose, settings, onSave }) => {
  const [localSettings, setLocalSettings] = useState(settings);

  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const handleSave = () => {
    onSave(localSettings);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Sync Settings</DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Batch Size"
              type="number"
              value={localSettings.sync_batch_size || 100}
              onChange={(e) => setLocalSettings(prev => ({ ...prev, sync_batch_size: parseInt(e.target.value) }))}
              helperText="Number of records to process in each batch"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Sync Timeout (seconds)"
              type="number"
              value={localSettings.sync_timeout || 300}
              onChange={(e) => setLocalSettings(prev => ({ ...prev, sync_timeout: parseInt(e.target.value) }))}
              helperText="Maximum time to wait for sync operations"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
};

// Main Sync Dashboard Component
const SyncDashboard = () => {
  const queryClient = useQueryClient();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedEntityType, setSelectedEntityType] = useState('courses');
  const [selectedIds, setSelectedIds] = useState([]);

  // Conditional polling - only when sync operations are running
  const { data: syncStatus, isLoading: statusLoading } = useQuery('syncStatus', api.getSyncStatus, {
    refetchInterval: (data) => {
      // Poll every 10 seconds if there are running operations, otherwise no polling
      return (data?.runningOperations > 0) ? 10000 : false;
    },
    refetchIntervalInBackground: true
  });
  
  // Check if there are running operations to enable conditional polling for other queries
  const hasRunningOperations = syncStatus?.runningOperations > 0;
  
  const { data: availableData, isLoading: dataLoading } = useQuery('availableData', api.getAvailableData, {
    refetchInterval: hasRunningOperations ? 30000 : false, // Poll only when operations are running
    refetchIntervalInBackground: true
  });
  
  const { data: syncHistory, isLoading: historyLoading } = useQuery('syncHistory', api.getSyncHistory, {
    refetchInterval: hasRunningOperations ? 15000 : false, // Poll history more frequently when operations are running
    refetchIntervalInBackground: true
  });
  
  const { data: syncSettings, isLoading: settingsLoading } = useQuery('syncSettings', api.getSyncSettings);

  // Mutations
  const startSyncMutation = useMutation(api.startSync, {
    onSuccess: () => {
      toast.success('Sync operation started successfully');
      queryClient.invalidateQueries('syncStatus');
      queryClient.invalidateQueries('syncHistory');
      queryClient.invalidateQueries('availableData');
    },
    onError: (error) => {
      toast.error(`Failed to start sync: ${error.response?.data?.message || error.message}`);
    }
  });

  const cancelSyncMutation = useMutation(api.cancelSync, {
    onSuccess: () => {
      toast.success('Sync operation cancelled successfully');
      queryClient.invalidateQueries('syncStatus');
      queryClient.invalidateQueries('syncHistory');
    },
    onError: (error) => {
      toast.error(`Failed to cancel sync: ${error.response?.data?.message || error.message}`);
    }
  });

  const updateSettingsMutation = useMutation(api.updateSyncSettings, {
    onSuccess: () => {
      toast.success('Settings updated successfully');
      queryClient.invalidateQueries('syncSettings');
    },
    onError: (error) => {
      toast.error(`Failed to update settings: ${error.response?.data?.message || error.message}`);
    }
  });

  const handleStartSync = (entityType, ids = []) => {
    startSyncMutation.mutate({ entityType, ids });
  };

  const handleCancelSync = (operationId) => {
    cancelSyncMutation.mutate(operationId);
  };

  const handleUpdateSettings = (newSettings) => {
    updateSettingsMutation.mutate(newSettings);
  };

  if (statusLoading || dataLoading || historyLoading || settingsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" sx={{ fontSize: '1.5rem', color: 'rgba(0, 0, 0, 0.87)' }}>
          Sync Management Dashboard
        </Typography>
        <Box>
          <Tooltip title="Sync Settings">
            <IconButton onClick={() => setSettingsOpen(true)}>
              <SettingsIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={() => queryClient.invalidateQueries()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Status Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <SyncStatusCard
            title="Total Courses"
            count={syncStatus?.data?.total_courses || 0}
            status={syncStatus?.data?.courses_sync_status}
            icon={<BookIcon />}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SyncStatusCard
            title="Total Groups"
            count={syncStatus?.data?.total_groups || 0}
            status={syncStatus?.data?.groups_sync_status}
            icon={<GroupIcon />}
            color="secondary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SyncStatusCard
            title="Total Students"
            count={syncStatus?.data?.total_students || 0}
            status={syncStatus?.data?.students_sync_status}
            icon={<PersonIcon />}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SyncStatusCard
            title="Last Sync"
            count={syncStatus?.data?.last_sync && syncStatus.data.last_sync !== null ? format(new Date(syncStatus.data.last_sync), 'MMM dd') : 'Never'}
            status={syncStatus?.data?.sync_enabled ? 'Enabled' : 'Disabled'}
            icon={<TimelineIcon />}
            color="info.main"
          />
        </Grid>
      </Grid>

      {/* Sync Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" mb={2}>
            🔄 Sync Operations
          </Typography>
          
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<SyncIcon />}
                onClick={() => handleStartSync('full')}
                disabled={startSyncMutation.isLoading}
              >
                Full Sync
              </Button>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<BookIcon />}
                onClick={() => handleStartSync('courses')}
                disabled={startSyncMutation.isLoading}
              >
                Sync Courses
              </Button>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<GroupIcon />}
                onClick={() => handleStartSync('groups')}
                disabled={startSyncMutation.isLoading}
              >
                Sync Groups
              </Button>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<PersonIcon />}
                onClick={() => handleStartSync('students')}
                disabled={startSyncMutation.isLoading}
              >
                Sync Students
              </Button>
            </Grid>
          </Grid>

          {/* Selective Sync */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" mb={2}>
            🎯 Selective Sync
          </Typography>
          
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Entity Type</InputLabel>
                <Select
                  value={selectedEntityType}
                  onChange={(e) => setSelectedEntityType(e.target.value)}
                  label="Entity Type"
                >
                  <MenuItem value="courses">Courses</MenuItem>
                  <MenuItem value="groups">Groups</MenuItem>
                  <MenuItem value="students">Students</MenuItem>
                  <MenuItem value="enrollments">Enrollments</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Selected IDs (comma-separated)"
                value={selectedIds.join(', ')}
                onChange={(e) => setSelectedIds(e.target.value.split(',').map(id => id.trim()).filter(Boolean))}
                placeholder="1, 2, 3, 4, 5"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                startIcon={<PlayArrowIcon />}
                onClick={() => handleStartSync(selectedEntityType, selectedIds)}
                disabled={startSyncMutation.isLoading || selectedIds.length === 0}
              >
                Sync Selected
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Current Sync Progress */}
      {syncHistory?.data?.find(op => op.status === 'running') && (
        <Box mb={3}>
          <Typography variant="h6" mb={2}>
            📊 Current Sync Progress
          </Typography>
          <SyncProgress 
            operation={syncHistory.data.find(op => op.status === 'running')} 
            onCancel={handleCancelSync}
          />
        </Box>
      )}

      {/* Sync History */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <SyncHistory history={syncHistory?.data || []} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>
                📈 Sync Statistics
              </Typography>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Total Operations: {syncHistory?.data?.length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Successful: {syncHistory?.data?.filter(op => op.status === 'completed').length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Failed: {syncHistory?.data?.filter(op => op.status === 'failed').length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Running: {syncHistory?.data?.filter(op => op.status === 'running').length || 0}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Settings Dialog */}
      <SyncSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={syncSettings?.data || {}}
        onSave={handleUpdateSettings}
      />
    </Box>
  );
};

export default SyncDashboard;

